import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { User, UserManager, type UserManagerSettings } from 'oidc-client-ts';

export interface OidcAuthState {
  readonly user: User | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

export interface OidcAuthSession extends OidcAuthState {
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  handleCallback: () => Promise<string | null>;
  getAccessToken: () => string | null;
}

export interface UseOidcAuthSessionOptions {
  readonly settings: UserManagerSettings;
  readonly onAccessTokenChanged?: (getToken: () => string | null) => void;
}

export function useOidcAuthSession(options: UseOidcAuthSessionOptions): OidcAuthSession {
  const managerRef = useRef<UserManager>(new UserManager(options.settings));
  const [state, setState] = useState<OidcAuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const manager = managerRef.current;

    const setUser = (user: User | null) => {
      setState({
        user,
        isAuthenticated: !!user && !user.expired,
        isLoading: false,
        error: null,
      });
    };

    manager
      .getUser()
      .then(setUser)
      .catch((error: unknown) => {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    const handleUserLoaded = (user: User) => setUser(user);
    const handleUserUnloaded = () => setUser(null);
    const handleAccessTokenExpired = () => {
      void manager.removeUser().finally(() =>
        manager.signinRedirect({
          state: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` },
        }),
      );
    };
    const handleSilentRenewError = (error: Error) => {
      const message = error.message.toLowerCase();
      const isDeadSession =
        message.includes('invalid_grant') ||
        message.includes('invalid_token') ||
        message.includes('session not active') ||
        message.includes('login_required');

      if (isDeadSession) {
        void manager.removeUser();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error,
        });
        return;
      }

      setState((previousState) => ({ ...previousState, error }));
    };

    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addUserUnloaded(handleUserUnloaded);
    manager.events.addAccessTokenExpired(handleAccessTokenExpired);
    manager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeUserUnloaded(handleUserUnloaded);
      manager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [options.settings]);

  const login = useCallback(async (returnTo?: string) => {
    const state = returnTo ? { returnTo } : undefined;
    await managerRef.current.signinRedirect({ state });
  }, []);

  const logout = useCallback(async () => {
    await managerRef.current.signoutRedirect();
  }, []);

  const handleCallback = useCallback(async (): Promise<string | null> => {
    const user = await managerRef.current.signinRedirectCallback();
    setState({
      user,
      isAuthenticated: !user.expired,
      isLoading: false,
      error: null,
    });

    const userState = user.state as { returnTo?: string } | string | null | undefined;
    if (typeof userState === 'string') {
      return userState;
    }

    return userState?.returnTo ?? null;
  }, []);

  const getAccessToken = useCallback(() => state.user?.access_token ?? null, [state.user]);

  useEffect(() => {
    options.onAccessTokenChanged?.(getAccessToken);
  }, [getAccessToken, options]);

  return useMemo(
    () => ({ ...state, login, logout, handleCallback, getAccessToken }),
    [state, login, logout, handleCallback, getAccessToken],
  );
}
